import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';

export interface PortalUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  orgId: number;
  orgName: string;
  domainName: string;
}

interface PortalAuthState {
  user: PortalUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (hostname: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (hostname: string, email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const res = await api.post('/support/portal/login', { email, password }, {
            headers: { 'x-helpdesk-domain': hostname },
          });
          const data = res.data?.data || res.data;
          const { token, user } = data;

          // Store portal token separately from staff token
          localStorage.setItem('portalToken', token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          set({ user, token, isAuthenticated: true, isLoading: false, error: null });
        } catch (e: any) {
          const msg = e?.response?.data?.message || 'Invalid email or password';
          set({ isLoading: false, error: msg, isAuthenticated: false });
          throw new Error(msg);
        }
      },

      logout: () => {
        localStorage.removeItem('portalToken');
        // Restore staff token if exists
        const staffToken = localStorage.getItem('accessToken');
        if (staffToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${staffToken}`;
        } else {
          delete api.defaults.headers.common['Authorization'];
        }
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'portal-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        // Restore portal token to axios on app load
        if (state?.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
        }
      },
    },
  ),
);
